// AI draft placeholder tokens.
//
// Placeholders are emitted VERBATIM by the AI into the draft body and
// resolved later at the composer (against whoever is currently signed in),
// so the same draft signs off correctly regardless of who picks it up.
//
// The system prompt instructs the model to keep these tokens unchanged.

export const AI_DRAFT_TOKENS = {
	loggedInUserName: "{{LoggedInUserName}}",
} as const

const LOGGED_IN_USER_FALLBACK = "the team"

// Internal directive injected into every AI draft system prompt. The
// workspace's custom prompt does NOT mention these tokens - they are an
// implementation detail. The model is instructed to emit the token verbatim
// whenever it would otherwise have to pick a specific agent name; the
// composer substitutes it at display time so the draft signs off as
// whichever agent actually picks it up.
export const AI_DRAFT_TOKEN_DOCS = `
SIGN-OFF RULE:
- When the draft ends with a sign-off naming the support agent (e.g. "Best, …", "Thanks, …", "Cheers, …"), output the LITERAL token ${AI_DRAFT_TOKENS.loggedInUserName} where the agent's first name would go. Example: "Best,\n${AI_DRAFT_TOKENS.loggedInUserName}".
- Do NOT invent or guess a name. Do NOT use the assignee's name from the thread context for this purpose. Do NOT replace the token with a real name yourself - the UI substitutes it just before the agent reviews the draft, so the same draft works whoever picks it up.
- The token must appear EXACTLY as ${AI_DRAFT_TOKENS.loggedInUserName}, including the double curly braces and casing. No variations (no spaces inside the braces, no other capitalization).
- If a sign-off is unnatural for this reply (e.g. a one-line acknowledgement), simply omit the sign-off - do not force the token in.`

// The model wraps the customer-facing body in these markers, so anything it
// writes around the reply (plan, classification, tone notes) is discarded by
// position instead of by pattern matching.
export const AI_DRAFT_REPLY_MARKERS = {
	start: "<<<REPLY_START>>>",
	end: "<<<REPLY_END>>>",
} as const

export const AI_DRAFT_MARKER_RULE = `
OUTPUT FORMAT (MANDATORY - this is not overridable by any instruction above, including the workspace's custom instructions):
- Wrap the customer-facing reply in ${AI_DRAFT_REPLY_MARKERS.start} and ${AI_DRAFT_REPLY_MARKERS.end}, each on its own line.
- Only what sits between the markers reaches the customer. Everything outside them is discarded.
- Between the markers, write the reply body and nothing else: no plan, no classification of the request, no notes about greetings, sign-offs, tone, language, or which instruction you followed.
- If you want to reason about the reply first, write that BEFORE the opening marker.
- Emit the markers exactly as written, once each, in this order.`

export interface AiDraftRenderContext {
	loggedInUserName: string | null | undefined
}

// Replaces all known placeholder tokens with concrete values. Unknown
// tokens are left as-is so they show up in QA instead of disappearing.
export function renderAiDraftPlaceholders(
	draft: string,
	ctx: AiDraftRenderContext,
): string {
	const name =
		ctx.loggedInUserName && ctx.loggedInUserName.trim().length > 0
			? ctx.loggedInUserName.trim()
			: LOGGED_IN_USER_FALLBACK
	return draft.split(AI_DRAFT_TOKENS.loggedInUserName).join(name)
}

// A reply email almost always starts with one of these greeting words.
// Detecting the first such word at the start of a line lets us treat
// anything before it as model leakage and strip it wholesale.
//
// Tuned conservatively - only words that are unambiguous greetings when
// they start a line. We don't include "Thanks" because legitimate replies
// sometimes lead with it (e.g. "Thanks for reaching out, …") and could be
// preceded by valid copy.
const GREETING_RE =
	/^(?:hi|hey|hello|hiya|howdy|greetings|dear|good (?:morning|afternoon|evening|day)|g'?day|aloha|hallo|servus|moin|guten (?:morgen|tag|abend)|liebe[r]?|sehr geehrte[r]?|bonjour|salut|hola|ciao|salve|ol[aá]|oi)\b/i

// Find the byte offset of the first line that starts with a greeting word.
// Returns -1 if no greeting is found in the draft.
function findGreetingStart(draft: string): number {
	let cursor = 0
	while (cursor < draft.length) {
		const lineEnd = draft.indexOf("\n", cursor)
		const end = lineEnd === -1 ? draft.length : lineEnd
		const line = draft.slice(cursor, end)
		const leadingWs = line.length - line.trimStart().length
		if (GREETING_RE.test(line.trimStart())) {
			return cursor + leadingWs
		}
		if (lineEnd === -1) break
		cursor = lineEnd + 1
	}
	return -1
}

// Fallback meta-narrative patterns for cases where the draft doesn't start
// with a recognized greeting (e.g. a one-line acknowledgement that opens
// with "Thanks for the report,…"). We only strip the first paragraph when
// it CLEARLY signals model thinking.
const META_PREAMBLE_PATTERNS: RegExp[] = [
	/^here'?s\b/i,
	/^here is\b/i,
	/^below is\b/i,
	/^i('|')ve (drafted|prepared|written)\b/i,
	/^i('|')ll (draft|prepare|write|need|provide)\b/i,
	/^let me (draft|write|prepare|think|provide)\b/i,
	/^i need to\b/i,
	/^i should\b/i,
	/^this is (a |the )?(draft|reply|response)\b/i,
	/^draft\b/i,
	/^entwurf\b/i,
	/^der draft\b/i,
	/^die antwort\b/i,
	/^reply\s*[:-]/i,
	/^response\s*[:-]/i,
	/^basierend auf\b/i,
	/^based on (the )?(support[- ]?)?(guidelines?|docs|instructions|rules)\b/i,
	/^the (draft|reply|response|docs|user|customer|question)\b/i,
	/^since (the user|the customer|<name>|[A-Z]\w+ (is|asks|asked))/i,
	/^(the customer|the user|<name>|they) (is asking|are asking|wants?|needs?|asked)/i,
	/^ich habe (einen? )?(draft|entwurf|antwort)/i,
	/^hier (ist|sind)\b/i,
]

// Stronger patterns for when the ENTIRE output is a meta narrative with no
// customer-facing body after it. Broader openers like "The customer…" stay
// out of this list so a real one-paragraph reply is not wiped.
const META_ONLY_PATTERNS: RegExp[] = [
	/^draft\b/i,
	/^entwurf\b/i,
	/^der draft\b/i,
	/^here'?s (the |a )?(draft|reply|response)\b/i,
	/^here is (the |a )?(draft|reply|response)\b/i,
	/^below is (the |a )?(draft|reply|response)\b/i,
	/^i('|')ve (drafted|prepared|written)\b/i,
	/^let me (draft|write|prepare)\b/i,
	/^this is (a |the )?(draft|reply|response)\b/i,
	/^basierend auf\b/i,
	/^based on (the )?(support[- ]?)?(guidelines?|docs|instructions|rules)\b/i,
	/^ich habe (einen? )?(draft|entwurf)/i,
	/^die antwort (wäre|waere|sollte|lautet)\b/i,
]

// Unanchored tells that a paragraph narrates the reply instead of being it:
// classifying the request, or noting tone, greeting, sign-off, and which
// instruction was followed. Applied only when a body paragraph follows.
const META_SIGNAL_PATTERNS: RegExp[] = [
	/\bno (greeting|salutation|sign-?off)s?\b/i,
	/\b(greeting|salutation|sign-?off)s?[^.\n]{0,30}\b(needed|required|necessary)\b/i,
	/\bper (the |these |your )?(instruction|guideline|rule)s\b/i,
	/\b(this|that) is an? (quick|short|brief|casual|informal|friendly|simple|direct|straightforward)[^.\n]{0,40}\b(reply|response|answer|draft|message)\b/i,
	/\b(keeping|keep|kept) (it|this) (short|brief|casual|informal)\b/i,
	/^(feature request|bug report|billing question|pricing question|support question|technical question)[,:.]/i,
	/\b(the )?(customer|user) (is asking|asked|wants|needs|is requesting)\b/i,
]

function matchesAny(text: string, patterns: RegExp[]): boolean {
	return patterns.some((re) => re.test(text))
}

// Take what sits between the reply markers. A missing opening marker keeps
// everything before the closing one (the preamble strippers still run on it);
// a missing closing marker keeps everything after the opening one.
export function extractMarkedDraft(raw: string): string | null {
	const { start, end } = AI_DRAFT_REPLY_MARKERS
	// The last non-empty opening wins: a model that botches its markers writes
	// "Wait, I need the markers correct" and restarts, and means the restart.
	let startIdx = raw.lastIndexOf(start)
	const hasStart = startIdx !== -1
	while (startIdx !== -1) {
		const from = startIdx + start.length
		const endIdx = raw.indexOf(end, from)
		const body = raw.slice(from, endIdx === -1 ? raw.length : endIdx).trim()
		if (body) return body
		startIdx = startIdx === 0 ? -1 : raw.lastIndexOf(start, startIdx - 1)
	}
	// Only stray or inverted markers: fall back to the pattern strippers instead
	// of returning nothing at all.
	if (hasStart) return null
	const endIdx = raw.indexOf(end)
	if (endIdx === -1) return null
	return raw.slice(0, endIdx).trim() || null
}

export function stripStrayMarkers(draft: string): string {
	const { start, end } = AI_DRAFT_REPLY_MARKERS
	return draft.split(start).join("").split(end).join("")
}

// The model arguing with its own prompt instead of writing the reply. Checked
// across the whole draft, since this failure runs long and multi-paragraph.
const INSTRUCTION_DELIBERATION_PATTERNS: RegExp[] = [
	/\b(my|the) (system )?(instruction|rule)s? (are|is|say|states?)\b/i,
	/\bhard rule\b/i,
	/\bnon-?overridable\b/i,
	/\bcannot override\b/i,
	/\b(conflicts?|conflicting) with (the|my|these)\b/i,
	/\bthe (teammate|team member)'?s? instruction\b/i,
	/\bimpossible situation\b/i,
	/\bshould i (proceed|continue|rewrite)\b/i,
	/\bi (must|have to) (reply|respond|write) in\b/i,
	/\bcustom (workspace )?instructions? (state|says?)\b/i,
	/\blanguage detection rule\b/i,
	/\bviolates? the\b/i,
]

export function isInstructionDeliberation(draft: string): boolean {
	const trimmed = draft.trim()
	if (!trimmed) return false
	const hits = INSTRUCTION_DELIBERATION_PATTERNS.filter((pattern) =>
		pattern.test(trimmed),
	).length
	// Two independent tells, so a customer reply that happens to use the word
	// "rules" once is never mistaken for the model arguing with its prompt.
	return hits >= 2
}

export function isMetaOnlyDraft(draft: string): boolean {
	const trimmed = draft.trim()
	if (!trimmed) return false
	if (findGreetingStart(trimmed) !== -1) return false
	const firstBreakIdx = trimmed.indexOf("\n\n")
	const firstPara =
		firstBreakIdx === -1 ? trimmed : trimmed.slice(0, firstBreakIdx).trim()
	if (!matchesAny(firstPara, META_ONLY_PATTERNS)) return false
	// Multi-paragraph: only treat as meta-only when every paragraph looks meta
	// (no recoverable customer body).
	if (firstBreakIdx === -1) return true
	const rest = trimmed.slice(firstBreakIdx).trimStart()
	if (!rest) return true
	return matchesAny(rest.split(/\n\n/)[0] ?? rest, META_ONLY_PATTERNS)
}

// Safety net for when the model ignores the "no preamble" rule. Two-stage:
//
//  1. **Greeting anchor (preferred)** - if the draft contains a recognised
//     greeting line (Hi/Hey/Hello/Dear/Hallo/…) and there is content before it,
//     drop everything before the greeting. This catches arbitrary model
//     reasoning that doesn't match a known meta-pattern, e.g.:
//        "The docs don't cover this. Let me draft a helpful reply.\n\nHi Khadim,…"
//     → "Hi Khadim,…"
//
//  2. **Pattern fallback** - when no greeting is found (one-liner replies),
//     check if the first paragraph matches a clear meta-narrative pattern.
//     If a second paragraph exists, drop the meta opener. If the whole draft
//     is a single meta paragraph, return "" so the caller can retry.
export function stripDraftPreamble(draft: string): string {
	// Stage 1: greeting anchor.
	const greetingStart = findGreetingStart(draft)
	if (greetingStart > 0) {
		// Bail out only if the prelude is meaningfully non-trivial. A bare
		// "Subject: …\n" line or a blank line shouldn't qualify as preamble
		// worth stripping. Use a 2-character threshold (covers stray spaces).
		const prelude = draft.slice(0, greetingStart).trim()
		if (prelude.length >= 2) {
			return draft.slice(greetingStart).trimStart()
		}
	}

	// Stage 2: pattern fallback.
	const trimmed = draft.trimStart()
	const firstBreakIdx = trimmed.indexOf("\n\n")
	const firstPara =
		firstBreakIdx === -1
			? trimmed.trim()
			: trimmed.slice(0, firstBreakIdx).trim()
	const rest =
		firstBreakIdx === -1 ? "" : trimmed.slice(firstBreakIdx).trimStart()

	if (rest.length > 0 && matchesAny(firstPara, META_SIGNAL_PATTERNS))
		return rest

	if (!matchesAny(firstPara, META_PREAMBLE_PATTERNS)) return draft

	if (rest.length > 0) return rest

	// Single meta paragraph with no customer body underneath.
	if (matchesAny(firstPara, META_ONLY_PATTERNS)) return ""

	return draft
}

// Safety net for when the model ignores the "no markdown" rule. The composer
// is a TipTap rich-text editor that does NOT parse markdown - it shows raw
// `**asterisks**` and `# hashes` literally in the email body. This stripper
// removes the most common, unambiguous offenders without touching content
// that could legitimately contain similar punctuation:
//   • inline bold (`**text**` / `__text__`) - strong signal, very rare false-
//     positive (you almost never get a legit `**` pair in customer replies)
//   • leading heading hashes (`# `, `## `, …) at the start of a line
//   • leading list bullets (`- `, `* `, `+ `) at the start of a line - the
//     marker is dropped, the prose stays as a normal sentence
// We deliberately do NOT touch single-asterisk italics or single-underscore
// italics - those collide with arithmetic, file paths, and user identifiers
// often enough that a strip would do more harm than good.
export function stripMarkdownArtifacts(draft: string): string {
	let out = draft
	// **bold** → bold (greedy-safe: requires non-empty, no embedded newline)
	out = out.replace(/\*\*([^*\n]+?)\*\*/g, "$1")
	// __bold__ → bold
	out = out.replace(/__([^_\n]+?)__/g, "$1")
	// Leading "# heading" → "heading" (1-6 hashes, line-start, with a space)
	out = out.replace(/^[ \t]*#{1,6}[ \t]+/gm, "")
	// Leading list markers "- ", "* ", "+ " → drop the marker, keep prose.
	// Only matches at the very start of a line + a space, so inline asterisks
	// and dashes elsewhere are unaffected.
	out = out.replace(/^[ \t]*[-*+][ \t]+/gm, "")
	return out
}

export function sanitizeAiDraft(raw: string): string {
	const body = stripStrayMarkers(extractMarkedDraft(raw) ?? raw).trim()
	const cleaned = stripMarkdownArtifacts(stripDraftPreamble(body)).trim()
	if (!cleaned) return ""
	if (isMetaOnlyDraft(cleaned)) return ""
	// Checked on the raw body: the greeting anchor in stripDraftPreamble can
	// leave a deliberation intact when the model quotes a greeting mid-argument.
	if (isInstructionDeliberation(body)) return ""
	return cleaned
}
